<script setup>
import { ref, computed, nextTick, watch, unref, useId } from 'vue';
import { useI18n } from 'vue-i18n';
import { vOnClickOutside } from '@vueuse/components';
import Select from 'dashboard/components-next/select/Select.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import {
  formatLocalizedDateInTimezone,
  parseLocalizedNumber,
  zonedDateTimeToUtc,
} from 'dashboard/routes/dashboard/pipeline/utils/localeFormatting';

const props = defineProps({
  modelValue: {
    type: [String, Number, Boolean],
    default: '',
  },
  type: {
    type: String,
    default: 'text',
    validator: v =>
      [
        'text',
        'multiline',
        'number',
        'currency',
        'date',
        'datetime',
        'list',
        'checkbox',
      ].includes(v),
  },
  // Dropdown values for type="list" (mirrors CustomAttributeDefinition#attribute_values).
  options: {
    type: Array,
    default: () => [],
  },
  placeholder: {
    type: String,
    default: '',
  },
  validator: {
    type: Function,
    default: null,
  },
  saving: {
    type: Boolean,
    default: false,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  displayClass: {
    type: String,
    default: '',
  },
  inputClass: {
    type: String,
    default: '',
  },
  accessibleLabel: {
    type: String,
    default: '',
  },
  currencyCode: {
    type: String,
    default: 'BRL',
  },
  currencySymbol: {
    type: String,
    default: '',
  },
  currencyLocale: {
    type: String,
    default: '',
  },
  locale: {
    type: String,
    default: '',
  },
  timeZone: {
    type: String,
    default: 'UTC',
  },
  emptyLabel: {
    type: String,
    default: '—',
  },
});

const emit = defineEmits(['update:modelValue', 'save', 'cancel']);

const { t, locale: i18nLocale } = useI18n();

const editing = ref(false);
const displayRef = ref(null);
const inputRef = ref(null);
const editControlRef = ref(null);
const localValue = ref(props.modelValue);
const validationError = ref('');
const uid = useId();
const fieldId = `inline-edit-field-${uid}`;
const errorId = `${fieldId}-error`;

watch(
  () => props.modelValue,
  val => {
    if (!editing.value) localValue.value = val;
  }
);

// ── Formatting ──────────────────────────────────────────────────────────────

const resolvedLocale = computed(() => {
  const currentLocale = unref(i18nLocale);
  return String(
    props.locale || props.currencyLocale || currentLocale || 'en-US'
  ).replace(/_/g, '-');
});

const resolvedAccessibleLabel = computed(
  () => props.accessibleLabel || t('PIPELINE.INLINE_EDIT.EDIT_HINT')
);

const canonicalNumber = value => {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  const text = String(value ?? '').trim();
  if (!text) return null;
  return /^-?\d+(?:\.\d+)?$/.test(text) ? Number(text) : null;
};

const parseCurrencyInput = value => {
  if (String(value ?? '') === String(props.modelValue ?? '')) {
    const canonical = canonicalNumber(value);
    if (canonical !== null) return canonical;
  }
  return parseLocalizedNumber(value, resolvedLocale.value, {
    currency: props.currencyCode,
    currencySymbol: props.currencySymbol,
  });
};

const formatCurrency = val => {
  const num = canonicalNumber(val) ?? parseCurrencyInput(val);
  if (num === null) return props.emptyLabel;

  try {
    const formatter = new Intl.NumberFormat(resolvedLocale.value, {
      style: 'currency',
      currency: props.currencyCode,
      currencyDisplay: 'symbol',
    });

    if (!props.currencySymbol) return formatter.format(num);

    return formatter
      .formatToParts(num)
      .map(part =>
        part.type === 'currency' ? props.currencySymbol : part.value
      )
      .join('');
  } catch {
    return new Intl.NumberFormat(resolvedLocale.value).format(num);
  }
};

const displayValue = computed(() => {
  const v = props.modelValue;
  if (v === null || v === undefined || v === '') return '';
  if (props.type === 'currency') return formatCurrency(v);
  if (props.type === 'date') {
    return formatLocalizedDateInTimezone(v, {
      locale: resolvedLocale.value,
      timeZone: props.timeZone,
      dateStyle: 'short',
    });
  }
  if (props.type === 'datetime') {
    const [date, time] = String(v).split('T');
    const instant = zonedDateTimeToUtc(date, time, props.timeZone);
    return instant
      ? formatLocalizedDateInTimezone(instant, {
          locale: resolvedLocale.value,
          timeZone: props.timeZone,
          dateStyle: 'short',
          timeStyle: 'short',
        })
      : '';
  }
  if (props.type === 'checkbox') {
    return v
      ? t('PIPELINE.INLINE_EDIT.CHECKBOX_YES')
      : t('PIPELINE.INLINE_EDIT.CHECKBOX_NO');
  }
  return String(v);
});

const isEmpty = computed(() => displayValue.value === '');

const displayAriaLabel = computed(() => {
  const value = isEmpty.value
    ? t('PIPELINE.INLINE_EDIT.EMPTY_VALUE')
    : displayValue.value;
  return `${resolvedAccessibleLabel.value}: ${value}`;
});

// ── List (dropdown) options ──────────────────────────────────────────────────

const selectOptions = computed(() =>
  props.options.map(option => ({ value: option, label: option }))
);

const focusEditControl = () => {
  const componentOrElement = inputRef.value || editControlRef.value;
  const rootElement = componentOrElement?.$el || componentOrElement;
  const focusableElement = rootElement?.matches?.(
    'input, textarea, select, button, [tabindex]:not([tabindex="-1"])'
  )
    ? rootElement
    : rootElement?.querySelector?.(
        'input, textarea, select, button, [tabindex]:not([tabindex="-1"])'
      );

  focusableElement?.focus();
};

const restoreDisplayFocus = () => {
  nextTick(() => displayRef.value?.focus());
};

// ── Enter edit mode ──────────────────────────────────────────────────────────

const startEdit = () => {
  if (props.disabled || props.saving) return;
  localValue.value = props.modelValue;
  validationError.value = '';
  editing.value = true;
  nextTick(() => {
    focusEditControl();
  });
};

// ── Validation & save ────────────────────────────────────────────────────────

const coercedValue = computed(() => {
  if (props.type === 'number') {
    if (String(localValue.value ?? '').trim() === '') return null;
    const value = Number(localValue.value);
    return Number.isFinite(value) ? value : Number.NaN;
  }
  if (props.type === 'currency') {
    if (String(localValue.value ?? '').trim() === '') return null;
    return parseCurrencyInput(localValue.value) ?? Number.NaN;
  }
  if (props.type === 'checkbox') return Boolean(localValue.value);
  return localValue.value;
});

const commitSave = ({ restoreFocus = false } = {}) => {
  if (
    ['number', 'currency'].includes(props.type) &&
    typeof coercedValue.value === 'number' &&
    !Number.isFinite(coercedValue.value)
  ) {
    validationError.value = t('PIPELINE.INLINE_EDIT.VALIDATION_ERROR');
    return;
  }
  if (props.validator) {
    const result = props.validator(coercedValue.value);
    if (result !== true) {
      validationError.value =
        typeof result === 'string'
          ? result
          : t('PIPELINE.INLINE_EDIT.VALIDATION_ERROR');
      return;
    }
  }
  validationError.value = '';
  editing.value = false;
  emit('update:modelValue', coercedValue.value);
  emit('save', coercedValue.value);
  if (restoreFocus) restoreDisplayFocus();
};

const cancelEdit = ({ restoreFocus = false } = {}) => {
  editing.value = false;
  localValue.value = props.modelValue;
  validationError.value = '';
  emit('cancel');
  if (restoreFocus) restoreDisplayFocus();
};

// list/checkbox não têm blur nativo como os inputs de texto (o texto usa @blur→
// commitSave). Sem uma saída, abrir um dropdown/checkbox e clicar fora deixava o
// campo "grudado" em edição. Escape e clique-fora abandonam a edição — só para
// esses dois tipos (os demais já tratam Escape via onKeydown e blur via onBlur).
const onEditDismiss = () => {
  if (editing.value && (props.type === 'list' || props.type === 'checkbox')) {
    cancelEdit();
  }
};

// `list`/`checkbox` commit as soon as a value is picked — there's no typing
// step to wait out with a blur listener like the native inputs below.
const selectListValue = value => {
  localValue.value = value;
  commitSave({ restoreFocus: true });
};

const toggleCheckboxValue = value => {
  localValue.value = value;
  commitSave({ restoreFocus: true });
};

// ── Keyboard handlers ────────────────────────────────────────────────────────

const onKeydown = e => {
  if (e.key === 'Escape') {
    e.preventDefault();
    e.stopPropagation();
    cancelEdit({ restoreFocus: true });
    return;
  }
  if (e.key === 'Enter' && props.type !== 'multiline') {
    e.preventDefault();
    commitSave({ restoreFocus: true });
  }
};

const onBlur = () => {
  // Small delay so a click on popover/tooltip doesn't conflict
  setTimeout(() => {
    if (editing.value) commitSave();
  }, 120);
};

// ── Display key handler ──────────────────────────────────────────────────────

const onDisplayKeydown = e => {
  if (e.key === 'Enter' || e.key === ' ') {
    e.preventDefault();
    startEdit();
  }
};

// ── Input native type ────────────────────────────────────────────────────────

const nativeInputType = computed(() => {
  if (props.type === 'number' || props.type === 'currency') return 'text';
  if (props.type === 'date') return 'date';
  if (props.type === 'datetime') return 'datetime-local';
  return 'text';
});
</script>

<template>
  <div class="relative inline-block w-full group/inline-field">
    <!-- ── DISPLAY mode ── -->
    <template v-if="!editing">
      <div
        ref="displayRef"
        :role="disabled ? undefined : 'button'"
        :tabindex="disabled ? undefined : 0"
        :aria-label="displayAriaLabel"
        class="flex items-center gap-1 min-h-11 cursor-pointer rounded px-1 -mx-1 hover:bg-n-alpha-1 transition-colors duration-100 select-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-brand focus-visible:ring-offset-2"
        :class="[
          displayClass,
          { 'opacity-50 cursor-default pointer-events-none': disabled },
        ]"
        @click="startEdit"
        @keydown="onDisplayKeydown"
      >
        <!-- Pre-line for multiline -->
        <span
          v-if="type === 'multiline' && !isEmpty"
          class="whitespace-pre-line text-n-slate-12 text-sm leading-snug flex-1"
        >
          {{ displayValue }}
        </span>

        <!-- Single-line or empty -->
        <span
          v-else
          class="text-sm leading-snug flex-1"
          :class="isEmpty ? 'text-n-slate-10 italic' : 'text-n-slate-12'"
        >
          {{ isEmpty ? emptyLabel : displayValue }}
        </span>

        <!-- Edit pencil icon (visible on hover unless disabled) -->
        <span
          v-if="!disabled"
          class="opacity-0 group-hover/inline-field:opacity-60 transition-opacity duration-100 text-n-slate-11 flex-shrink-0"
          aria-hidden="true"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="12"
            height="12"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path
              d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 1 2-2v-7"
            />
            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
          </svg>
        </span>
      </div>
    </template>

    <!-- ── EDIT mode ── -->
    <template v-else>
      <div
        v-on-click-outside="onEditDismiss"
        class="relative"
        @keydown.esc.stop.prevent="cancelEdit({ restoreFocus: true })"
      >
        <!-- Multiline textarea -->
        <textarea
          v-if="type === 'multiline'"
          :id="fieldId"
          ref="inputRef"
          v-model="localValue"
          :aria-label="resolvedAccessibleLabel"
          :aria-invalid="validationError ? 'true' : undefined"
          :aria-describedby="validationError ? errorId : undefined"
          :placeholder="placeholder || t('PIPELINE.INLINE_EDIT.SAVE_HINT')"
          :disabled="saving"
          rows="2"
          class="w-full min-h-11 resize-none overflow-hidden rounded-md px-2 py-2 text-sm text-n-slate-12 bg-n-solid-2 border border-n-weak focus:border-n-brand focus:ring-1 focus:ring-n-brand/20 focus:outline-none disabled:opacity-50 [field-sizing:content]"
          :class="inputClass"
          @keydown="onKeydown"
          @blur="onBlur"
        />

        <!-- List (dropdown) -->
        <div
          v-else-if="type === 'list'"
          ref="editControlRef"
          class="w-full [&>div]:w-full"
        >
          <Select
            :model-value="localValue"
            :options="selectOptions"
            :input-id="fieldId"
            :aria-label="resolvedAccessibleLabel"
            :aria-describedby="validationError ? errorId : ''"
            :error="validationError"
            :placeholder="placeholder || t('PIPELINE.INLINE_EDIT.SELECT_HINT')"
            :disabled="saving"
            class="[&_select]:w-full"
            @update:model-value="selectListValue"
          />
        </div>

        <!-- Checkbox (boolean). The outer button provides a 44px target while
             the inner track keeps the compact visual proportions. -->
        <button
          v-else-if="type === 'checkbox'"
          :id="fieldId"
          ref="editControlRef"
          type="button"
          role="switch"
          :aria-label="resolvedAccessibleLabel"
          :aria-checked="Boolean(localValue)"
          :aria-invalid="validationError ? 'true' : undefined"
          :aria-describedby="validationError ? errorId : undefined"
          :disabled="saving"
          class="inline-flex min-h-11 min-w-11 items-center justify-center rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-brand focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
          @click="toggleCheckboxValue(!Boolean(localValue))"
        >
          <span
            aria-hidden="true"
            class="relative inline-flex h-4 w-7 flex-shrink-0 rounded-full transition-colors duration-200 motion-reduce:transition-none"
            :class="Boolean(localValue) ? 'bg-n-brand' : 'bg-n-slate-6'"
          >
            <span
              class="absolute top-0.5 size-3 rounded-full bg-white shadow-sm transition-transform duration-200 motion-reduce:transition-none"
              :class="
                Boolean(localValue) ? 'translate-x-3.5' : 'translate-x-0.5'
              "
            />
          </span>
        </button>

        <!-- Single-line input -->
        <Input
          v-else
          :id="fieldId"
          ref="inputRef"
          v-model="localValue"
          :type="nativeInputType"
          :aria-label="resolvedAccessibleLabel"
          :aria-invalid="validationError ? 'true' : undefined"
          :aria-describedby="validationError ? errorId : undefined"
          :placeholder="placeholder"
          :disabled="saving"
          :custom-input-class="[
            '!h-11 text-sm text-n-slate-12 bg-n-solid-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-brand',
            inputClass,
          ]"
          @keydown="onKeydown"
          @blur="onBlur"
        />

        <!-- Saving spinner -->
        <span
          v-if="saving"
          role="status"
          class="absolute right-2 top-1/2 -translate-y-1/2"
          :aria-label="t('PIPELINE.INLINE_EDIT.SAVING')"
        >
          <svg
            class="h-3.5 w-3.5 text-n-brand motion-safe:animate-spin motion-reduce:animate-none"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              class="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              stroke-width="4"
            />
            <path
              class="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8v8H4z"
            />
          </svg>
        </span>
      </div>

      <!-- Validation error tooltip -->
      <p
        v-if="validationError"
        :id="errorId"
        role="alert"
        class="mt-1 text-xs text-n-ruby-9 font-medium"
      >
        {{ validationError }}
      </p>

      <!-- Hint (not applicable to list/checkbox — those save on selection) -->
      <p
        v-else-if="type !== 'list' && type !== 'checkbox'"
        class="mt-0.5 text-xs text-n-slate-10"
      >
        {{ t('PIPELINE.INLINE_EDIT.SAVE_HINT') }}
      </p>
    </template>
  </div>
</template>
